import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { convertGoogleLatLng, getAddressFromCoordinates } from '@core/utils/location/addressHandler.utils';
import { setUpdateCoverageBoundary, setUpdateCoverageLayer } from '@core/store/modules/isochronous/isochronous.slice';
import { FIELDER } from '@core/constants/base.constants';
import { setCenter, setZoom } from '@core/store/modules/map/mapSlice';
import { selectUpdatedLocation } from '@core/store/modules/isochronous/isochronous.selectors';
import { useLanguage } from 'Context/LanguageContext';
import { FielderHard } from '@core/theme/baseTheme';

const styleDefault = {
	strokeColor: '#810FCB',
	strokeOpacity: 1.0,
	strokeWeight: 2.0,
	fillColor: 'white',
	fillOpacity: 0.1,
};
const styleClicked = {
	...styleDefault,
	fillColor: '#810FCB',
	fillOpacity: 0.5,
};
const styleMouseMove = {
	...styleDefault,
	strokeWeight: 4.0,
};
const coverageGoogle = FIELDER.coverageByGoogle;
const coverageSelect = FIELDER.coverageSelect;
async function getAddressNameForFeature(placeId, featureType, options = { returnInfo: false }) {
	if (!window.google || !placeId) return options.returnInfo ? null : '';
	const tryPlaces = () =>
		new Promise((resolve) => {
			try {
				const svc = new google.maps.places.PlacesService(document.createElement('div'));
				svc.getDetails({ placeId, fields: ['address_components', 'name', 'formatted_address'] }, (res, status) => {
					if (status !== google.maps.places.PlacesServiceStatus.OK || !res) resolve(null);
					else resolve(res);
				});
			} catch {
				resolve(null);
			}
		});
	const tryGeocode = () =>
		new Promise((resolve) => {
			try {
				const geo = new google.maps.Geocoder();
				geo.geocode({ placeId }, (results, status) => {
					if (status !== 'OK' || !results || !results.length) resolve(null);
					else resolve(results[0]);
				});
			} catch {
				resolve(null);
			}
		});
	const res = (await tryPlaces()) || (await tryGeocode());
	if (!res) return options.returnInfo ? null : '';
	const comps = res.address_components || [];
	const findComp = (type) => comps.find((ac) => ac.types?.includes(type));
	const pick = (type) => {
		const c = findComp(type);
		return { long: c?.long_name || '', short: c?.short_name || '' };
	};
	const nonEmpty = (arr) => arr.filter(Boolean);
	const joinComma = (arr) => nonEmpty(arr).join(', ');
	const countryComp = pick(coverageGoogle.country);
	const admin1Comp = pick(coverageGoogle.level1);
	const admin2Comp = pick(coverageGoogle.level2);
	const countryName = countryComp.long || countryComp.short;
	const countryCode = countryComp.short;
	let label = '';
	if (featureType === coverageSelect.country) {
		label = countryName || res.name || res.formatted_address || '';
	} else if (featureType === coverageSelect.level1) {
		label = joinComma([admin1Comp.long || admin1Comp.short, countryName]) || res.name || res.formatted_address || '';
	} else if (featureType === coverageSelect.level2) {
		label =
			joinComma([admin2Comp.long || admin2Comp.short, admin1Comp.long || admin1Comp.short, countryName]) ||
			res.name ||
			res.formatted_address ||
			'';
	} else {
		label = res.name || res.formatted_address || '';
	}
	if (!options.returnInfo) return label;
	return {
		label,
		countryCode,
		countryName,
		admin1: admin1Comp.long || admin1Comp.short || '',
		admin1Code: admin1Comp.short || '',
		admin2: admin2Comp.long || admin2Comp.short || '',
		admin2Code: admin2Comp.short || '',
	};
}
const Coverages = ({ coverage, googleMap, onSelect }) => {
	const lan = useLanguage();
	const dispatch = useDispatch();
	const updatedLocation = useSelector(selectUpdatedLocation);
	const featureLayerRef = useRef(null);
	const lastInteractedFeatureIdsRef = useRef([]);
	const lastClickedFeatureIdsRef = useRef([]);
	const currentLayerTypeRef = useRef(null);
	const [showOverlay, setShowOverlay] = useState(false);
	const applyStyle = (params) => {
		const placeId = params.feature.placeId;
		if (lastClickedFeatureIdsRef.current.includes(placeId)) return styleClicked;
		if (lastInteractedFeatureIdsRef.current.includes(placeId)) return styleMouseMove;
		return styleDefault;
	};
	const handleClick = (e) => {
		lastClickedFeatureIdsRef.current = e.features.map((f) => f.placeId);
		lastInteractedFeatureIdsRef.current = [];
		if (featureLayerRef.current) featureLayerRef.current.style = applyStyle;
		setShowOverlay(false);
		createVertices(e);
	};
	const handleMouseMove = (e) => {
		lastInteractedFeatureIdsRef.current = e.features.map((f) => f.placeId);
		if (featureLayerRef.current) featureLayerRef.current.style = applyStyle;
	};
	const clearHoverOnMapMouseMove = () => {
		if (lastInteractedFeatureIdsRef.current?.length) {
			lastInteractedFeatureIdsRef.current = [];
			if (featureLayerRef.current) featureLayerRef.current.style = applyStyle;
		}
	};
	const createVertices = async (event) => {
		if (!googleMap) return;
		const feature = event.features[0];
		if (!feature?.placeId) return;
		const place = await feature.fetchPlace();
		const placeId = feature.placeId;
		const placeName = place.displayName || place.name;
		const coordinates = convertGoogleLatLng(event.latLng);
		const getInfoCoverage = await getAddressNameForFeature(placeId, currentLayerTypeRef.current, { returnInfo: true });
		const address = getInfoCoverage.label;
		const countryCode = getInfoCoverage.countryCode;
		dispatch(setUpdateCoverageBoundary({ placeId, placeName, coordinates, address, countryCode }));
		let polygonPath = [];
		if (feature.geometry?.toGeoJson) {
			const geoJson = await new Promise((resolve) => feature.geometry.toGeoJson(resolve));
			if (geoJson?.type === 'Polygon' && geoJson.coordinates?.length)
				polygonPath = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
		}
		if (!polygonPath.length && place?.geometry?.coordinates?.length)
			polygonPath = place.geometry.coordinates[0]?.map(([lng, lat]) => ({ lat, lng })) || [];
		const callback = onSelect || coverage?.onSelect;
		if (callback) callback(polygonPath);
	};
	useEffect(() => {
		if (coverage?.featureLayer) dispatch(setUpdateCoverageLayer({ featureLayer: coverage.featureLayer }));
	}, [dispatch, coverage?.featureLayer]);
	useEffect(() => {
		if (coverage?.featureLayer) dispatch(setUpdateCoverageLayer({ featureLayer: coverage.featureLayer }));
	}, [dispatch, coverage?.featureLayer]);

	useEffect(() => {
		setShowOverlay(coverage?.enabled && !updatedLocation?.placeId);
	}, [coverage?.enabled, updatedLocation?.placeId]);
	useEffect(() => {
		if (!googleMap) return;
		const teardownLayer = () => {
			try {
				if (featureLayerRef.current) {
					google.maps.event.clearListeners(featureLayerRef.current, 'click');
					google.maps.event.clearListeners(featureLayerRef.current, 'mousemove');
					featureLayerRef.current.style = null;
				}
			} catch (e) {
				// no-op
			}
			featureLayerRef.current = null;
			currentLayerTypeRef.current = null;
			lastClickedFeatureIdsRef.current = [];
			lastInteractedFeatureIdsRef.current = [];
			google.maps.event.clearListeners(googleMap, 'mousemove');
		};
		const shouldEnableCoverage = coverage && coverage.enabled === true && coverage.featureLayer;
		if (!shouldEnableCoverage) {
			teardownLayer();
			return;
		}
		const targetLayerType = coverage.featureLayer || null;
		if (!targetLayerType) {
			teardownLayer();
			return;
		}
		if (currentLayerTypeRef.current && currentLayerTypeRef.current !== targetLayerType) teardownLayer();
		if (!featureLayerRef.current) {
			featureLayerRef.current = googleMap.getFeatureLayer(targetLayerType);
			currentLayerTypeRef.current = targetLayerType;
			featureLayerRef.current.addListener('click', handleClick);
			featureLayerRef.current.addListener('mousemove', handleMouseMove);
			googleMap.addListener('mousemove', clearHoverOnMapMouseMove);
			featureLayerRef.current.style = applyStyle;
		} else {
			featureLayerRef.current.style = applyStyle;
		}
	}, [googleMap, coverage?.enabled, coverage?.featureLayer]);

	useEffect(() => {
		if (!googleMap) return;
		const placeId = updatedLocation?.placeId;
		if (!placeId) return;

		const fitBoundary = async () => {
			try {
				let polygonPath = [];
				let place = null;
				if (window.google) {
					try {
						const svc = new google.maps.places.PlacesService(document.createElement('div'));
						place = await new Promise((resolve) => {
							svc.getDetails({ placeId, fields: ['geometry'] }, (res, status) => {
								if (status !== google.maps.places.PlacesServiceStatus.OK || !res) resolve(null);
								else resolve(res);
							});
						});
					} catch {
						place = null;
					}
				}
				if (place?.geometry?.coordinates?.length)
					polygonPath = place.geometry.coordinates[0]?.map(([lng, lat]) => ({ lat, lng })) || [];
				let bounds = null;
				if (polygonPath.length) {
					bounds = new google.maps.LatLngBounds();
					polygonPath.forEach((coord) => bounds.extend(coord));
				} else if (place?.geometry?.viewport) bounds = place.geometry.viewport;
				if (bounds) {
					googleMap.fitBounds(bounds);
					dispatch(setCenter(googleMap.getCenter().toJSON()));
					dispatch(setZoom(googleMap.getZoom()));
				}
			} catch {
				// no-op
			}
		};
		fitBoundary();
	}, [googleMap, coverage?.featureLayer, updatedLocation?.placeId, dispatch]);
	const overlayStyle = {
		position: 'absolute',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
		backgroundColor: FielderHard,
		color: '#fff',
		padding: '8px 12px',
		borderRadius: '4px',
		pointerEvents: 'none',
		zIndex: 1,
	};
	return showOverlay ? <div style={overlayStyle}>{lan.clickRegionToSelectCoverage}</div> : null;
};
Coverages.propTypes = {
	coverage: PropTypes.object,
	googleMap: PropTypes.object,
	onSelect: PropTypes.func,
};
export default React.memo(Coverages);
